import React, { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const InstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(isIOSDevice);

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return;
    }

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      console.log('beforeinstallprompt event received!');
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      setDeferredPrompt(promptEvent);
      // Store it globally so we can access it even if component re-renders
      (window as any).deferredInstallPrompt = promptEvent;
      console.log('Deferred prompt stored');
    };

    // Listen for manual install trigger from menu
    const handleShowInstall = () => {
      console.log('Install prompt triggered manually');
      console.log('Is iOS:', isIOSDevice);

      // Try to get the deferred prompt from state or global
      const prompt = deferredPrompt || (window as any).deferredInstallPrompt;
      console.log('Has deferred prompt:', !!prompt);

      if (prompt) {
        // If we have the prompt, trigger it immediately
        prompt.prompt().then(() => {
          prompt.userChoice.then((choiceResult: { outcome: string }) => {
            console.log('User choice:', choiceResult.outcome);
            if (choiceResult.outcome === 'accepted') {
              console.log('User accepted the install prompt');
            }
            // Clear the prompt
            setDeferredPrompt(null);
            (window as any).deferredInstallPrompt = null;
          });
        }).catch((err: Error) => {
          console.error('Error showing install prompt:', err);
        });
      } else {
        // No prompt available, show instructions modal
        setShowPrompt(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('showInstallPrompt', handleShowInstall);

    console.log('InstallPrompt component mounted, listening for events');

    // Don't auto-show on iOS, only show when manually triggered
    // Users can trigger it from the menu

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('showInstallPrompt', handleShowInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      console.log('No deferred prompt available');
      return;
    }

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        console.log('App installed');
      } else {
        console.log('App installation dismissed');
      }
    } catch (error) {
      console.error('Error during install prompt:', error);
    }

    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Don't save dismiss state - allow users to trigger it again from menu
  };

  if (!showPrompt) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      left: '20px',
      right: '20px',
      padding: '16px',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      zIndex: 9999,
      maxWidth: '400px',
      margin: '0 auto'
    }}>
      {isIOS ? (
        <div>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '18px' }}>Install Bradanna Field App</h3>
          <p style={{ margin: '0 0 12px 0', fontSize: '14px' }}>
            To install: Tap the share button <span style={{ fontSize: '20px' }}>⬆️</span> then
            "Add to Home Screen" <span style={{ fontSize: '20px' }}>➕</span>
          </p>
          <button
            onClick={handleDismiss}
            style={{
              padding: '8px 16px',
              backgroundColor: '#f0f0f0',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Got it
          </button>
        </div>
      ) : deferredPrompt ? (
        <div>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '18px' }}>Install Bradanna Field App</h3>
          <p style={{ margin: '0 0 12px 0', fontSize: '14px' }}>
            Install the app for quick access and offline use
          </p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleInstall}
              style={{
                flex: 1,
                padding: '10px',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Install
            </button>
            <button
              onClick={handleDismiss}
              style={{
                flex: 1,
                padding: '10px',
                backgroundColor: '#f0f0f0',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Not Now
            </button>
          </div>
        </div>
      ) : (
        <div>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '18px' }}>Install Bradanna Field App</h3>
          <p style={{ margin: '0 0 12px 0', fontSize: '14px' }}>
            Your browser doesn't support app installation, or the app is already installed.
          </p>
          <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#666' }}>
            If you're using Chrome or Edge, you can install the app from the browser menu (⋮) → "Install app"
          </p>
          <button
            onClick={handleDismiss}
            style={{
              width: '100%',
              padding: '10px',
              backgroundColor: '#f0f0f0',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
};